import React, { FC, useState } from 'react';
import { IUserWithTokens } from '../../../helpers/types';
import './UsersList.scss';
import RectButton from '../../atoms/RectButton/RectButton';
import Pagination from '../../atoms/Pagination/Pagination';

import Tag from '../../atoms/Tag/Tag';
import { getRandomColor } from '../../../helpers/functions';
import Select from '../../atoms/Select/Select';
import Input from "../Input/Input";

interface UsersListProps {
    items: IUserWithTokens[];
    totalCount: number;
    currentPage: number;
    pageUrl: string;
    onChangePagination: (page: number) => void;
    onUpdateUser: (id: number, data: { login: string; password?: string }) => void | Promise<void>;
    onDeleteToken: (userId: number, tokenId: number) => void | Promise<void>;
    onCreateUser: (login: string, password: string, role: string) => void | Promise<void>;
    onChangeRole: (userId: number, role: string) => void | Promise<void>;
}

const ITEMS_PER_PAGE = 10;

const roleOptions = [
    { value: 'USER', name: 'USER' },
    { value: 'ADMIN', name: 'ADMIN' },
];

const UsersList: FC<UsersListProps> = ({
                                           items,
                                           totalCount,
                                           currentPage,
                                           pageUrl,
                                           onChangePagination,
                                           onUpdateUser,
                                           onDeleteToken,
                                           onCreateUser,
                                           onChangeRole,
                                       }) => {
    const [openedId, setOpenedId] = useState<number | null>(null);
    const [loginValue, setLoginValue] = useState<string>('');
    const [passwordValue, setPasswordValue] = useState<string>('');
    const [roleValue, setRoleValue] = useState<string>('USER');

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newLogin, setNewLogin] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [newRole, setNewRole] = useState<string>('USER');

    const handleToggleAccordion = (user: IUserWithTokens) => {
        if (openedId === user.id) {
            setOpenedId(null);
            setLoginValue('');
            setPasswordValue('');
        } else {
            setOpenedId(user.id);
            setLoginValue(user.login);
            setPasswordValue('');
            const firstRole =
                user.roles && user.roles.length > 0 ? user.roles[0].title : 'USER';
            setRoleValue(firstRole);
        }
    };

    const handleSubmit = (e: React.FormEvent, userId: number) => {
        e.preventDefault();
        onUpdateUser(userId, {
            login: loginValue,
            password: passwordValue || undefined,
        });
    };

    const handleCreateUser = () => {
        if (!newLogin || !newPassword) {
            return;
        }
        Promise.resolve(onCreateUser(newLogin, newPassword, newRole)).then(() => {
            setNewLogin('');
            setNewPassword('');
            setNewRole('USER');
            setIsCreateModalOpen(false);
        });
    };

    const handleSetRole = (userId: number) => {
        if (!roleValue) {
            return;
        }
        onChangeRole(userId, roleValue);
    };

    const formatDate = (date: string) => {
        const d = new Date(date);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}.${mm}.${yy} ${hh}:${min}`;
    };

    return (
        <div className="users">
            <div className="users__header">
                <h3 className="users__title">Пользователи</h3>
                <RectButton
                    type="button"
                    name="Создать пользователя"
                    onClick={() => setIsCreateModalOpen(true)}
                />
            </div>

            {isCreateModalOpen && (
                <div className="users-modal" onClick={() => setIsCreateModalOpen(false)}>
                    <div
                        className="users-modal__content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4 className="users-modal__title">Создать пользователя</h4>
                        <div className="users-modal__row">
                            <Input
                                name="newLogin"
                                placeholder="Логин"
                                value={newLogin}
                                onChange={(e) => setNewLogin(e.target.value)}
                            />
                        </div>
                        <div className="users-modal__row">
                            <Input
                                type="password"
                                name="newPassword"
                                placeholder="Пароль"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                        </div>
                        <div className="users-modal__row">
                            <Select
                                name="newRole"
                                placeholder="Роль"
                                items={roleOptions}
                                defaultValue={newRole}
                                onChange={(value) => setNewRole(value)}
                            />
                        </div>
                        <div className="users-modal__actions">
                            <RectButton
                                type="button"
                                name="Создать"
                                onClick={handleCreateUser}
                            />
                            <RectButton
                                type="button"
                                name="Отмена"
                                onClick={() => setIsCreateModalOpen(false)}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="users-table">
                <div className="users-table__header">
                    <div className="users-table__cell users-table__cell--id">ID</div>
                    <div className="users-table__cell">Логин</div>
                    <div className="users-table__cell">Роли</div>
                    <div className="users-table__cell">Количество токенов</div>
                    <div className="users-table__cell users-table__cell--actions"></div>
                </div>

                <div className="users-table__body">
                    {items && items.length > 0 ? (
                        items.map((user) => (
                            <div className="users-table__item" key={user.id}>
                                <div className="users-table__row">
                                    <div className="users-table__cell users-table__cell--id">
                                        {user.id}
                                    </div>
                                    <div className="users-table__cell">{user.login}</div>
                                    <div className="users-table__cell">
                                        {user.roles &&
                                            user.roles.map((role) => (
                                                <Tag
                                                    key={role.id}
                                                    name={role.title}
                                                    color={getRandomColor(role.title)}
                                                />
                                            ))}
                                    </div>
                                    <div className="users-table__cell">
                                        {user.tokens ? user.tokens.length : 0}
                                    </div>
                                    <div className="users-table__cell users-table__cell--actions">
                                        <RectButton
                                            type="button"
                                            name="Edit"
                                            onClick={() => handleToggleAccordion(user)}
                                        />
                                    </div>
                                </div>

                                <div
                                    className={
                                        'users-table__accordion ' +
                                        (openedId === user.id
                                            ? 'users-table__accordion--open'
                                            : '')
                                    }
                                >
                                    <div className="users-table__accordion-inner">
                                        <div className="users-table__accordion-rows">
                                            <form
                                                className="users-table__form"
                                                onSubmit={(e) => handleSubmit(e, user.id)}
                                            >
                                                <div className="users-table__form-row">
                                                    <Input
                                                        name="login"
                                                        placeholder="Логин"
                                                        value={loginValue}
                                                        onChange={(e) => setLoginValue(e.target.value)}
                                                    />
                                                </div>
                                                <div className="users-table__form-row">
                                                    <Input
                                                        type="password"
                                                        name="password"
                                                        placeholder="Новый пароль (опционально)"
                                                        value={passwordValue}
                                                        onChange={(e) => setPasswordValue(e.target.value)}
                                                    />
                                                </div>
                                                <div className="users-table__form-actions">
                                                    <RectButton type="button" name="Сохранить" />
                                                    <RectButton
                                                        type="button"
                                                        name="Отмена"
                                                        onClick={() => {
                                                            setOpenedId(null);
                                                            setLoginValue('');
                                                            setPasswordValue('');
                                                        }}
                                                    />
                                                </div>
                                            </form>

                                            <div className="users-table__roles">
                                                <div className="users-table__roles-row">
                                                    <Select
                                                        name="role"
                                                        placeholder="Роль"
                                                        items={roleOptions}
                                                        defaultValue={roleValue}
                                                        onChange={(value) => setRoleValue(value)}
                                                    />
                                                    <RectButton
                                                        type="button"
                                                        name="Назначить роль"
                                                        onClick={() => handleSetRole(user.id)}
                                                    />
                                                </div>
                                            </div>
                                        </div>


                                        <div className="users-table__tokens">
                                            <h4 className="users-table__tokens-title">Токены</h4>
                                            {user.tokens && user.tokens.length > 0 ? (
                                                <div className="users-table__tokens-table">
                                                    <div className="users-table__tokens-header">
                                                        <div className="users-table__tokens-cell users-table__tokens-cell--id">
                                                            ID
                                                        </div>
                                                        <div className="users-table__tokens-cell">IP</div>
                                                        <div className="users-table__tokens-cell">Город</div>
                                                        <div className="users-table__tokens-cell">
                                                            Устройство
                                                        </div>
                                                        <div className="users-table__tokens-cell">
                                                            Дата создания
                                                        </div>
                                                        <div className="users-table__tokens-cell users-table__tokens-cell--actions"></div>
                                                    </div>
                                                    <div className="users-table__tokens-body">
                                                        {user.tokens.map((token) => (
                                                            <div
                                                                className="users-table__tokens-row"
                                                                key={token.id}
                                                            >
                                                                <div className="users-table__tokens-cell users-table__tokens-cell--id">
                                                                    {token.id}
                                                                </div>
                                                                <div className="users-table__tokens-cell users-table__tokens-cell--token">
                                                                    {token.ip}
                                                                </div>
                                                                <div className="users-table__tokens-cell">
                                                                    {token.city}
                                                                </div>
                                                                <div className="users-table__tokens-cell">
                                                                    {token.device}
                                                                </div>
                                                                <div className="users-table__tokens-cell">
                                                                    <Tag
                                                                        name={
                                                                            token.createdAt
                                                                                ? formatDate(token.createdAt)
                                                                                : ''
                                                                        }
                                                                        color="#104100"
                                                                    />
                                                                </div>
                                                                <div className="users-table__tokens-cell users-table__tokens-cell--actions">
                                                                    <RectButton
                                                                        type="button"
                                                                        name="Delete"
                                                                        onClick={() =>
                                                                            onDeleteToken(user.id, token.id)
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="users-table__tokens-empty">
                                                    Нет токенов
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="users-table__empty">Нет пользователей</div>
                    )}
                </div>
            </div>

            <div className="users__pagination">
                <Pagination
                    totalItems={totalCount}
                    itemsPerPage={ITEMS_PER_PAGE}
                    currentPage={currentPage}
                    onPageChange={onChangePagination}
                    pageUrl={pageUrl}
                />
            </div>
        </div>
    );
};

export default UsersList;
